import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth-context";
import { homeFor } from "@/components/RoleGuard";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function StaffLogin() {
  const navigate = useNavigate();
  const { user, signInWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(homeFor(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api("/auth/staff-login", {
        method: "POST",
        json: { email, password, totp_code: totp || null },
      });
      if (res.requires_totp) {
        setNeedTotp(true);
        toast.info("Enter your 2FA code");
        return;
      }
      await signInWithToken(res.session_token, res.user);
      navigate(homeFor(res.user.role), { replace: true });
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary p-6">
      <Card className="w-full max-w-md rounded-2xl p-8">
        <Brand className="mb-6" />
        <h2 className="font-display text-2xl">Staff & admin</h2>
        <p className="mt-1 text-sm text-muted-foreground">Sign in with your credentials.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="staff-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="staff-password-input"
            />
          </div>
          {needTotp && (
            <div className="space-y-1.5">
              <Label htmlFor="totp">2FA code</Label>
              <Input
                id="totp"
                inputMode="numeric"
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                placeholder="123456"
                data-testid="staff-totp-input"
              />
            </div>
          )}
          <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="staff-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm">
          <button className="text-primary hover:underline" onClick={() => navigate("/login")} data-testid="back-to-login">
            Back to customer login
          </button>
        </div>
      </Card>
    </div>
  );
}
